import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Password from "@hudson/ui/components/FormFields/Password";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import Success from "@hudson/ui/components/Success";
import Session from "@lib/session";
import useContent from "@lib/use_content";
import savePassword from "@services/save_password";
import { useState } from "react";
import PasswordStrengthBar from "react-password-strength-bar";
import NewPassword from "../shared/NewPassword";

export default function ChangePassword() {
  const { data: cData, loading } = useContent([
    "beChangepwNewPasswordLabel",
    "beChangepwPasswordLabel",
    "beProfilePasswordTitle",
    "beChangepwSubmitButton",
    "beChangepwSuccessMsg",
    "beErrFieldRequired",
    "beErrPasswordUpdate",
    "bePasswordAriaHide",
    "bePasswordAriaShow",
    "beRequiredPlaceholder",
  ]);
  const formMethods = useForm({});
  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>();
  const [submitting, setSubmitting] = useState(false);
  const inputPassword = formMethods.watch("password");

  interface ChangeData {
    currentPassword: string;
    newPassword: string;
  }

  async function submitForm(data: ChangeData) {
    const userId = Session.getBooking().profiles.user;
    if (!userId) return;

    setErrorMessage("");
    setSubmitting(true);

    const { success, errorMessage } = await savePassword(
      userId,
      data.currentPassword,
      data.newPassword,
    );

    if (!success) {
      if (errorMessage) {
        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(cData?.content.beErrPasswordUpdate);
      }
    } else {
      formMethods.reset();
      setSuccess(true);
    }

    setSubmitting(false);
  }

  return (
    <>
      {loading ? (
        <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        <FormProvider {...formMethods}>
          <form
            id="change-password"
            onSubmit={(e) => {
              e.preventDefault();
              formMethods.handleSubmit(submitForm)();
            }}
            noValidate
          >
            <div className="max-w-xs divide-y divide-gray-200">
              <div>
                <div className="mt-8 mb-6 space-y-1">
                  <h3 className="text-xl font-bold leading-6 text-gray-900">
                    {cData?.content.beProfilePasswordTitle}
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-y-6 ">
                  {
                    <>
                      <Password
                        errorMessage={cData?.content.beErrFieldRequired}
                        fieldName="currentPassword"
                        hideLabel={cData?.content.bePasswordAriaHide}
                        label={cData?.content.beChangepwPasswordLabel}
                        requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                        showLabel={cData?.content.bePasswordAriaShow}
                      />
                      <NewPassword
                        autoComplete="new-password"
                        errorMessage={cData?.content.beErrFieldRequired}
                        fieldName="newPassword"
                        hideLabel={cData?.content.bePasswordAriaHide}
                        label={cData?.content.beChangepwNewPasswordLabel}
                        requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                        showLabel={cData?.content.bePasswordAriaShow}
                      />
                      <PasswordStrengthBar className="w-64 -mt-4" password={inputPassword} />

                      <div className="">
                        {errorMessage && (
                          <Error className="mb-6" message={errorMessage} heavyStyle scrollTo />
                        )}
                        {success && (
                          <Success
                            className="mb-6"
                            message={cData?.content.beChangepwSuccessMsg}
                            heavyStyle
                            scrollTo
                          />
                        )}
                        <ContinueButton
                          alignLeft
                          className="mt-2 justify-self-start"
                          disabled={submitting}
                          label={cData?.content.beChangepwSubmitButton}
                          loading={submitting}
                        />
                      </div>
                    </>
                  }
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </>
  );
}
